import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowRight, Calendar } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InsightsPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to load insights");
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);

    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Successfully subscribed to our newsletter!");
      setEmail("");
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to subscribe. Please try again.");
      }
    } finally {
      setSubscribing(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen noise-texture pt-32">
      {/* Hero Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-brandSecondary font-medium text-sm uppercase tracking-wider" data-testid="insights-label">
              Insights & Articles
            </span>
            <h1 className="font-heading font-bold text-5xl md:text-6xl text-white mt-4 mb-6" data-testid="insights-heading">
              Marketing Intelligence
              <br />
              <span className="text-gradient">Worth Your Time</span>
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed" data-testid="insights-intro">
              Strategic insights on AI, marketing, and growth. No fluff, no hype—just actionable intelligence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Newsletter Subscription */}
      <section className="py-12 px-6" data-testid="newsletter-section">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-8 text-center"
          >
            <h3 className="font-heading font-semibold text-2xl text-white mb-3" data-testid="newsletter-heading">
              Get Weekly Insights
            </h3>
            <p className="text-gray-400 mb-6" data-testid="newsletter-description">
              Strategic marketing intelligence delivered every week. Unsubscribe anytime.
            </p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="newsletter-email-input"
                className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-full text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brandPrimary focus:border-transparent"
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="newsletter-submit-button"
                className="bg-brandPrimary hover:bg-brandPrimary/90 text-white px-8 py-3 rounded-full font-medium transition-all disabled:opacity-50"
              >
                {subscribing ? "Subscribing..." : "Subscribe"}
              </button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-20 px-6" data-testid="posts-section">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center text-gray-400" data-testid="loading-state">
              <p>Loading insights...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center" data-testid="empty-state">
              <p className="text-gray-400 text-lg mb-4">No insights published yet.</p>
              <p className="text-gray-500">Check back soon for strategic marketing intelligence.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, idx) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  data-testid={`post-card-${idx}`}
                >
                  <Link to={`/insights/${post.slug}`} className="block group">
                    <div className="glass-card rounded-2xl p-6 h-full hover:border-brandPrimary/50 transition-all">
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-4">
                        <span className="px-3 py-1 bg-brandPrimary/10 text-brandPrimary rounded-full text-xs font-medium">
                          {post.category}
                        </span>
                        <div className="flex items-center gap-1">
                          <Clock size={14} />
                          <span>{post.read_time} min</span>
                        </div>
                      </div>
                      
                      <h3 className="font-heading font-semibold text-xl text-white mb-3 group-hover:text-brandPrimary transition-colors">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-400 text-sm leading-relaxed mb-4">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-white/10">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar size={14} />
                          <span>{formatDate(post.created_at)}</span>
                        </div>
                        <div className="flex items-center gap-1 text-brandPrimary text-sm font-medium group-hover:gap-2 transition-all">
                          Read More
                          <ArrowRight size={16} />
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default InsightsPage;